'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import {
  BsHouseDoor,
  BsTrophy,
  BsFileText,
  BsBullseye,
  BsBarChart,
  BsThreeDots,
  BsPeople,
  BsShieldLock,
  BsFileEarmarkText,
  BsStar,
  BsX,
} from 'react-icons/bs'

const mainTabs = [
  { href: '/', label: 'Home', icon: BsHouseDoor, match: (p: string) => p === '/' },
  { href: '/rate', label: 'Rate It', icon: BsStar, match: (p: string) => p.startsWith('/rate') },
  { href: '/best', label: 'Best Of', icon: BsTrophy, match: (p: string) => p.startsWith('/best') },
  { href: '/blog', label: 'Blog', icon: BsFileText, match: (p: string) => p.startsWith('/blog') },
  {
    href: '/challenges',
    label: 'Challenges',
    icon: BsBullseye,
    match: (p: string) => p.startsWith('/challenges'),
  },
  {
    href: '/dashboard/user',
    label: 'Dashboard',
    icon: BsBarChart,
    match: (p: string) => p.startsWith('/dashboard/user') || p.startsWith('/profile'),
  },
] as const

const otherLinks = [
  { href: '/dashboard/community', label: 'Community', icon: BsPeople },
  { href: '/privacy', label: 'Privacy', icon: BsShieldLock },
  { href: '/terms', label: 'Terms', icon: BsFileEarmarkText },
] as const

function isOtherActive(pathname: string) {
  return otherLinks.some((l) => pathname.startsWith(l.href))
}

export function MobileBottomNav() {
  const pathname = usePathname()
  const [otherOpen, setOtherOpen] = useState(false)

  useEffect(() => {
    setOtherOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!otherOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOtherOpen(false)
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [otherOpen])

  const otherActive = isOtherActive(pathname)

  return (
    <>
      {otherOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/50"
            aria-label="Close menu"
            onClick={() => setOtherOpen(false)}
          />
          <div
            className="absolute bottom-0 left-0 right-0 rounded-t-2xl bg-white shadow-2xl border-t border-slate-200 pb-[env(safe-area-inset-bottom)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-other-title"
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <h2 id="mobile-other-title" className="text-base font-bold text-slate-800">
                More
              </h2>
              <button
                type="button"
                onClick={() => setOtherOpen(false)}
                className="p-2 -mr-2 text-slate-500 hover:text-slate-800"
                aria-label="Close"
              >
                <BsX className="text-2xl" />
              </button>
            </div>
            <nav className="px-3 pb-4 space-y-1">
              {otherLinks.map((link) => {
                const Icon = link.icon
                const active = pathname.startsWith(link.href)
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setOtherOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl font-medium transition-colors ${
                      active
                        ? 'bg-[#1E4289]/10 text-[#1E4289]'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="text-xl flex-shrink-0" />
                    <span>{link.label}</span>
                  </Link>
                )
              })}
            </nav>
          </div>
        </div>
      )}

      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#1E4289] border-t border-[#0f2654] pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_8px_rgba(0,0,0,0.15)]"
        aria-label="Primary"
      >
        <ul className="grid grid-cols-7 h-16">
          {mainTabs.map((tab) => {
            const Icon = tab.icon
            const active = tab.match(pathname)
            return (
              <li key={tab.href} className="min-w-0">
                <Link
                  href={tab.href}
                  className={`flex flex-col items-center justify-center gap-0.5 h-full px-0.5 text-[10px] font-semibold transition-colors ${
                    active ? 'text-[#FEDB1A]' : 'text-white/75 hover:text-white'
                  }`}
                >
                  <Icon className="text-xl flex-shrink-0" />
                  <span className="truncate max-w-full leading-tight">{tab.label}</span>
                </Link>
              </li>
            )
          })}
          <li className="min-w-0">
            <button
              type="button"
              onClick={() => setOtherOpen(true)}
              className={`flex flex-col items-center justify-center gap-0.5 h-full w-full px-0.5 text-[10px] font-semibold transition-colors ${
                otherActive || otherOpen ? 'text-[#FEDB1A]' : 'text-white/75 hover:text-white'
              }`}
              aria-expanded={otherOpen}
              aria-haspopup="dialog"
            >
              <BsThreeDots className="text-xl flex-shrink-0" />
              <span className="truncate max-w-full leading-tight">Other</span>
            </button>
          </li>
        </ul>
      </nav>
    </>
  )
}
